import React, { Component, createRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Icon, TypingIndicator } from "../../components/Icon";
import { urlMap, getAuthHeaders, partsToPlainText } from "../../../utils";
import CoachComposer from "../../components/coach/CoachComposer";
import { NotificationContext } from "../../../state/notifications/NotificationContext";

/**
 * ProjectCoachPanel – project-level coach chat that always uses the master model.
 *
 * No provider/model selector — it fetches the master model on mount and uses it
 * for every session and message.
 *
 * Props:
 *  - projectId: string    – current project UUID
 *  - onClose: () => void  – called when the user closes the panel
 */
export default class ProjectCoachPanel extends Component {
  static contextType = NotificationContext;
  readerAbortController = null;
  textareaRef = createRef();

  state = {
    sessionId: null,
    masterModelId: null,
    masterModelName: null,
    chatMessages: [],
    loading: true,
    error: null,
    sending: false,
    typing: false,
    input: "",
    includeChapterContent: false,
    seedsUsed: 0,
    viewMode: "chat", // "chat" | "sessions"
    allSessions: [],
    sessionsPage: 1,
    deletingSessionId: null,
    confirmDeleteSessionId: null,
  };

  SESSIONS_PER_PAGE = 10;
  MAX_INITIAL_SESSIONS = 3;

  // ── Lifecycle ────────────────────────────────────────────

  componentDidMount() {
    this.init();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.projectId !== this.props.projectId && this.props.projectId) {
      this.init();
    }
    // Keep focus on the composer after a message finishes sending
    if (prevState.sending && !this.state.sending) {
      this.textareaRef.current?.focus();
    }
  }

  componentWillUnmount() {
    try { this.readerAbortController?.abort(); } catch {}
  }

  async init() {
    this.setState({ loading: true, error: null });
    const masterModelId = await this.fetchMasterModel();
    if (!masterModelId) return;
    await this.ensureSessionAndLoad(masterModelId);
  }

  // ── Master model ─────────────────────────────────────────

  async fetchMasterModel() {
    try {
      const res = await fetch(urlMap.listLlms() + "/master", {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const model = data?.model;
      if (!model?.id) throw new Error("No master model configured");

      this.setState({ masterModelId: model.id, masterModelName: model.name || model.id });
      return model.id;
    } catch (err) {
      this.setState({ error: err.message || "Failed to load master model", loading: false });
      return null;
    }
  }

  // ── Session helpers ──────────────────────────────────────

  async ensureSessionAndLoad(modelId) {
    const { projectId } = this.props;
    if (!projectId) return;

    const sessions = await this.fetchSessions(modelId, projectId);
    let sessionId = sessions?.[0]?.id || null;

    if (!sessionId) {
      const created = await this.createSession(modelId, projectId);
      sessionId = created?.id || null;
    }

    if (sessionId) {
      this.setState({ sessionId }, () => this.fetchMessages(sessionId));
    } else {
      this.setState({ loading: false, error: "Could not create session" });
    }
  }

  async fetchSessions(modelId, projectId) {
    try {
      const url = new URL(urlMap.listProjectChatSessions(projectId), window.location.origin);
      if (modelId) url.searchParams.append("model", modelId);

      const res = await fetch(url.toString(), {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ error: err.message || "Failed to load sessions" });
      return [];
    }
  }

  async createSession(modelId, projectId) {
    const { includeChapterContent } = this.state;
    try {
      const res = await fetch(urlMap.createProjectChatSession(projectId), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ llmId: modelId, title: "Coach", includeChapterContent }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ error: err.message || "Failed to create session" });
      return null;
    }
  }

  async fetchMessages(sessionId) {
    try {
      const res = await fetch(urlMap.listChatMessages() + "?sessionId=" + sessionId, {
        method: "GET",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const visible = data.filter((m) => m.role !== "system");
      const bubbles = visible.map((m) => ({
        id: m.id,
        from: m.role === "user" ? "me" : "them",
        text: partsToPlainText(m.content),
      }));
      this.setState({ chatMessages: bubbles, loading: false, error: null });
    } catch (err) {
      this.setState({ error: err.message || "Failed to load messages", loading: false });
    }
  }

  // ── Sending ──────────────────────────────────────────────

  async sendUserMessage(sessionId, text) {
    const { projectId } = this.props;
    const { masterModelId } = this.state;

    try {
      const res = await fetch(urlMap.createProjectChatMessage(projectId), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({
          sessionId,
          llmId: masterModelId,
          content: [{ type: "text", text }],
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return await res.json();
    } catch (err) {
      this.setState({ error: err.message || "Failed to send message" });
      return null;
    }
  }

  onSend = async () => {
    const { sessionId, input } = this.state;
    const text = input.trim();
    if (!sessionId || !text) return;

    this.setState((s) => ({
      sending: true,
      typing: true,
      input: "",
      chatMessages: [...s.chatMessages, { id: `local-${Date.now()}`, from: "me", text }],
    }));

    const run = await this.sendUserMessage(sessionId, text);
    if (!run?.runId) {
      this.setState({ sending: false, typing: false });
      return;
    }

    await this.streamAssistantSSE(sessionId, run.runId);
  };

  // ── Streaming ────────────────────────────────────────────

  async streamAssistantSSE(sessionId, runId) {
    const streamUrl = new URL(urlMap.streamRun(runId), window.location.origin);
    streamUrl.searchParams.set("sessionId", sessionId);

    const draftId = `assist-${runId}`;
    this.setState((s) => ({
      chatMessages: [...s.chatMessages, { id: draftId, from: "them", text: "" }],
    }));

    this.readerAbortController = new AbortController();

    try {
      const res = await fetch(streamUrl.toString(), {
        method: "GET",
        headers: { ...getAuthHeaders(), Accept: "text/event-stream" },
        signal: this.readerAbortController.signal,
      });

      if (!res.ok || !res.body) {
        this.updateBubble(draftId, "⚠️ Stream failed.");
        this.setState({ sending: false });
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assembled = "";
      let firstChunk = true;

      const feed = (chunk) => {
        buffer += chunk;
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";

        for (const frame of frames) {
          for (const line of frame.split("\n")) {
            const t = line.trim();
            if (!t.startsWith("data:")) continue;
            try {
              const evt = JSON.parse(t.slice(5).trim());
              const kind = evt.event ?? evt.type;
              const delta = evt.delta ?? evt.payload?.delta;

              if (kind === "delta" && delta?.type === "text" && typeof delta.text === "string") {
                assembled += delta.text;
                this.updateBubble(draftId, assembled);
              } else if (kind === "done") return true;
            } catch {}
          }
        }
        return false;
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        if (firstChunk && chunk) {
          this.setState({ typing: false });
          firstChunk = false;
        }
        if (feed(chunk)) break;
      }
      if (buffer) feed(decoder.decode());

      this.updateBubble(draftId, assembled || "(no response)");
      this.setState({ typing: false });

      // Charge 1 seed only when there is an actual response
      if (assembled.trim()) {
        this.setState(s => ({ seedsUsed: s.seedsUsed + 1 }));
        this.context?.notifyWalletRefresh?.();
      }
    } catch (err) {
      if (err?.name !== "AbortError") this.updateBubble(draftId, "⚠️ Connection lost.");
      this.setState({ typing: false });
    } finally {
      this.setState({ sending: false });
      this.readerAbortController = null;
    }
  }

  updateBubble(id, text) {
    this.setState(
      (s) => ({ chatMessages: s.chatMessages.map((m) => (m.id === id ? { ...m, text } : m)) }),
      () => {
        const el = document.querySelector(".coach-messages");
        if (el) el.scrollTop = el.scrollHeight;
      }
    );
  }

  // ── Reset ────────────────────────────────────────────────

  onReset = async () => {
    const { projectId } = this.props;
    const { masterModelId } = this.state;
    if (!projectId || !masterModelId) return;

    const created = await this.createSession(masterModelId, projectId);
    const sessionId = created?.id || null;

    if (sessionId) {
      this.setState({ sessionId, chatMessages: [], loading: true }, () =>
        this.fetchMessages(sessionId)
      );
    } else {
      this.setState({ error: "Failed to reset chat" });
    }
  };

  // ── Input handling ───────────────────────────────────────

  onChange = (e) => {
    this.setState({ input: e.target.value }, () => {
      const el = this.textareaRef.current;
      if (el) {
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  };

  onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.onSend();
    }
  };

  // ── Session browsing ─────────────────────────────────────

  showSessions = async () => {
    this.setState({ viewMode: "sessions", sessionsPage: 1 });
    if (this.state.allSessions.length === 0) {
      const { projectId } = this.props;
      const { masterModelId } = this.state;
      const sessions = await this.fetchSessions(masterModelId, projectId);
      this.setState({ allSessions: sessions });
    }
  };

  backToChat = () => {
    this.setState({ viewMode: "chat" });
  };

  switchSession = (sessionId) => {
    this.setState({ sessionId, viewMode: "chat" }, () => {
      this.fetchMessages(sessionId);
    });
  };

  loadMoreSessions = () => {
    this.setState((s) => ({ sessionsPage: s.sessionsPage + 1 }));
  };

  nextSessionsPage = () => {
    this.setState((s) => ({ sessionsPage: s.sessionsPage + 1 }));
  };

  prevSessionsPage = () => {
    this.setState((s) => ({ sessionsPage: Math.max(1, s.sessionsPage - 1) }));
  };

  deleteSession = async (sessionId) => {
    const { projectId } = this.props;
    const { masterModelId, sessionId: activeSessionId } = this.state;
    if (!sessionId || !projectId) return;

    this.setState({ deletingSessionId: sessionId });
    try {
      const res = await fetch(urlMap.deleteChatSession(sessionId), {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const refreshed = await this.fetchSessions(masterModelId, projectId);
      const nextSessions = Array.isArray(refreshed) ? refreshed : [];

      this.setState((s) => {
        const totalPages = Math.max(1, Math.ceil(nextSessions.length / this.SESSIONS_PER_PAGE));
        return {
          allSessions: nextSessions,
          sessionsPage: Math.min(s.sessionsPage, totalPages),
        };
      });

      if (activeSessionId === sessionId) {
        if (nextSessions[0]?.id) {
          this.setState({ sessionId: nextSessions[0].id, chatMessages: [], loading: true }, () => {
            this.fetchMessages(nextSessions[0].id);
          });
        } else if (masterModelId) {
          const created = await this.createSession(masterModelId, projectId);
          if (created?.id) {
            this.setState({ sessionId: created.id, chatMessages: [], loading: true }, () => {
              this.fetchMessages(created.id);
            });
          }
        }
      }
    } catch {
      // Keep silent to avoid noisy UI; existing error handlers cover chat operations.
    } finally {
      this.setState({ deletingSessionId: null, confirmDeleteSessionId: null });
    }
  };

  // ── Render ───────────────────────────────────────────────

  render() {
    const { onClose } = this.props;
    const {
      chatMessages,
      masterModelName,
      loading,
      error,
      sending,
      typing,
      input,
      seedsUsed,
      viewMode,
      allSessions,
      sessionsPage,
      deletingSessionId,
      confirmDeleteSessionId,
    } = this.state;

    // Compute session list pagination
    const totalPages = Math.ceil(allSessions.length / this.SESSIONS_PER_PAGE);
    const startIdx = (sessionsPage - 1) * this.SESSIONS_PER_PAGE;
    const endIdx = startIdx + this.SESSIONS_PER_PAGE;
    const visibleSessions = allSessions.slice(startIdx, endIdx);

    const showLoadMore = allSessions.length > this.MAX_INITIAL_SESSIONS && sessionsPage === 1;
    const initialSessions = showLoadMore ? allSessions.slice(0, this.MAX_INITIAL_SESSIONS) : visibleSessions;

    return (
      <div className="coach-scroll">
        <aside className="coach-panel" aria-label="Project coach chat">
          {/* ── Header ── */}
          <div className="coach-header">
            {viewMode === "sessions" && (
              <button className="btn subtle" onClick={this.backToChat} title="Back to chat">
                ← Back
              </button>
            )}
            {viewMode === "chat" && (
              <span className="coach-model-label" title="storyclub coach">
                Storyclub coach
              </span>
            )}
            <span style={{ flex: 1 }} />
            {viewMode === "chat" && (
              <>
                <button className="btn subtle" onClick={this.showSessions} title="View session history">
                  History
                </button>
                <button className="btn subtle" onClick={this.onReset} title="Restart coach">
                  <Icon.Restart />
                </button>
              </>
            )}
            <button className="btn subtle" onClick={onClose} title="Close coach">
              <Icon.Close />
            </button>
          </div>

          {/* ── Chat View ── */}
          {viewMode === "chat" && (
            <>
              <div className="coach-messages">
                {loading && <div className="coach-bubble them">Loading…</div>}
                {error && <div className="coach-bubble them">⚠ {error}</div>}
                {chatMessages.map((m) => (
                  <div key={m.id} className={`coach-bubble ${m.from === "me" ? "me" : "them"}`}>
                    <ReactMarkdown className="coach-markdown" remarkPlugins={[remarkGfm]}>
                      {m.text}
                    </ReactMarkdown>
                  </div>
                ))}
                {typing && <TypingIndicator />}
              </div>

              <CoachComposer
                textareaRef={this.textareaRef}
                input={input}
                sending={sending}
                onChange={this.onChange}
                onKeyDown={this.onKeyDown}
                onSend={this.onSend}
                seedsUsed={seedsUsed}
              />
            </>
          )}

          {/* ── Sessions View ── */}
          {viewMode === "sessions" && (
            <div className="coach-messages coach-sessions-list">
              {allSessions.length === 0 ? (
                <div className="coach-bubble them">No previous sessions</div>
              ) : (
                <>
                  {visibleSessions.map((session) => {
                    const excerpt = session.last_message_preview || session.summary || null;
                    const trimmed = excerpt
                      ? excerpt.replace(/[#*`_~>\[\]]/g, "").trim().slice(0, 80) + (excerpt.length > 80 ? "…" : "")
                      : null;
                    return (
                      <div key={session.id} className="coach-session-item coach-session-row">
                        <button
                          className="coach-session-open"
                          onClick={() => this.switchSession(session.id)}
                        >
                          <span className="coach-session-title">{session.title || "Untitled"}</span>
                          <span className="coach-session-date">
                            {new Date(session.created_at).toLocaleDateString()}
                          </span>
                          {trimmed && (
                            <span className="coach-session-excerpt">{trimmed}</span>
                          )}
                        </button>
                        <button
                          className="btn subtle coach-session-delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            this.setState({ confirmDeleteSessionId: session.id });
                          }}
                          disabled={deletingSessionId === session.id}
                          title="Delete session"
                        >
                          Delete
                        </button>
                        {confirmDeleteSessionId === session.id && (
                          <div className="coach-session-confirm-inline" role="alert">
                            <span className="coach-session-confirm-text">Delete session?</span>
                            <button
                              className="btn subtle coach-session-confirm-btn danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.deleteSession(session.id);
                              }}
                              disabled={deletingSessionId === session.id}
                            >
                              {deletingSessionId === session.id ? "Deleting..." : "Confirm"}
                            </button>
                            <button
                              className="btn subtle coach-session-confirm-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                this.setState({ confirmDeleteSessionId: null });
                              }}
                              disabled={deletingSessionId === session.id}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {showLoadMore && (
                    <button
                      className="btn subtle coach-load-more-btn"
                      onClick={this.loadMoreSessions}
                    >
                      Load More
                    </button>
                  )}
                  {!showLoadMore && totalPages > 1 && (
                    <div className="coach-pagination">
                      <button
                        className="btn subtle"
                        onClick={this.prevSessionsPage}
                        disabled={sessionsPage === 1}
                      >
                        ← Prev
                      </button>
                      <span className="coach-pagination-info">
                        Page {sessionsPage} of {totalPages}
                      </span>
                      <button
                        className="btn subtle"
                        onClick={this.nextSessionsPage}
                        disabled={sessionsPage === totalPages}
                      >
                        Next →
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </aside>
      </div>
    );
  }
}
